import {
  Box,
  Pagination,
  styled,
  SxProps,
  TableCell,
  tableCellClasses,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Trans } from "react-i18next";

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.secondary.main,
    color: "#6B7280",
    padding: "0px 16px",
    height: "45px",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  [`&.${tableCellClasses.body}`]: {
    backgroundColor: "#ffffff",
    padding: "16px",
    height: "62px",
    borderBottom: "1px solid",
    borderColor: "#E5E7EB",
    fontSize: "14px",
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
}));

export const TableContainerWrapper = styled(Box)(() => ({
  backgroundColor: "#ffffff",
  border: "1px solid #E5E7EB",
  borderRadius: "20px",
  overflow: "hidden",
  width: "100%",
  padding: "16px 20px",
  boxShadow:
    "0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)",
}));

export const TableBorderRadiusTopLeftRight: SxProps = {
  "& .MuiTableHead-root th:first-of-type": {
    borderTopLeftRadius: "8px",
    borderBottomLeftRadius: "8px",
  },
  "& .MuiTableHead-root th:last-of-type": {
    borderTopRightRadius: "8px",
    borderBottomRightRadius: "8px",
  },
};

export const StyledSortLabel = styled(TableSortLabel)(({ theme }) => ({
  "& .MuiTableSortLabel-icon": {
    opacity: 0.5,
    color: "text.secondary",
  },
}));

export const TablePaginationStyle: SxProps = {
  backgroundColor: "#ffffff",
  borderBottom: "none",
  "& 	.MuiTablePagination-toolbar": {
    height: "78px",
    paddingLeft: 2.5,
    "& 	.MuiTablePagination-spacer": {
      display: "flex",
      flex: "none",
      order: 3,
    },
    "& 	.MuiTablePagination-actions": {
      display: "flex",
      gap: 1,
      order: 1,
      marginLeft: 0,
      marginRight: 1,
      color: "text.tertiary",
      "& .MuiButtonBase-root": {
        padding: 0,
        "& .MuiSvgIcon-root": {
          width: "34px",
          height: "34px",
        },
      },
    },
    "& .MuiTablePagination-displayedRows": {
      display: "flex",
      order: 2,
    },
    "& .MuiTablePagination-selectLabel": {
      order: 0,
      margin: 0,
      marginRight: 1,
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "160%",
    },
    "& .MuiTablePagination-input": {
      order: 0,
      marginRight: 3,
    },
    "& .MuiTablePagination-select": {
      display: "flex",
      alignItems: "center",
      backgroundColor: "#F3F4F6",
      border: "1px solid #E5E7EB",
      borderRadius: "6px",
      padding: "6px 28px 6px 10px",
      minHeight: "unset",
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: "160%",
    },
    "& .MuiTablePagination-selectIcon": {
      right: 6,
    },
  },
};

export const paginationLabel = ({ from, to, count }: any) => {
  return (
    <Typography variant="body1" component="span" color="text.grey">
      <Trans
        ns="common"
        i18nKey="PAGINATION_SHOW_OF"
        values={{ from, to, count }}
        components={{
          range: (
            <Typography
              component="span"
              fontWeight="regular"
              color="text.tertiary"
            />
          ),
          total: (
            <Typography
              component="span"
              fontWeight="regular"
              color="text.tertiary"
            />
          ),
        }}
      />
    </Typography>
  );
};

export const rowsPerPageLabel = (
  <Trans ns="common" i18nKey="PAGINATION_ROWS_PER_PAGE" />
);

export const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: theme.palette.secondary.main,
    borderTopLeftRadius: "8px",
    borderTopRightRadius: "8px",
    color: "#355962",
    fontSize: "14px",
    fontWeight: 600,
    borderBottom: "none",
    "& .MuiDataGrid-columnHeader": {
      "&:focus , &:focus-within": {
        outline: "none",
      },
    },
  },
  "& .MuiDataGrid-iconSeparator": {
    display: "none",
  },
  "& .MuiDataGrid-cell": {
    backgroundColor: "#ffffff",
    borderBottom: "1px solid ",
    borderColor: "#E5E7EB",
    color: "text.primary",
    fontSize: "14px",
    fontWeight: 500,
    "&:focus , &:focus-within": {
      outline: "none",
    },
  },
  "& .MuiDataGrid-checkboxInput": {
    color: "#9CA3AF",
  },
  "& .MuiDataGrid-footerContainer": {
    flexDirection: "row-reverse",
    backgroundColor: "#ffffff",
    height: "76px",
  },
}));

export const CustomPagination = styled(Pagination)(() => ({
  "& .MuiPagination-ul": {
    "& .MuiButtonBase-root": {
      height: "35px",
      backgroundColor: "#ffffff",
      color: "text.secondary",
      fontWeight: 500,
      margin: 0,
      borderRadius: 0,
      border: "1px solid #D1D5DB",
    },
    "& .Mui-selected": {
      backgroundColor: "secondary.main",
      color: "primary.main",
      fontWeight: 600,
    },
    "& .MuiPaginationItem-previousNext": {
      padding: "0 1.25rem",
    },
    "& li:first-of-type": {
      "& .MuiButtonBase-root": {
        borderTopLeftRadius: "6px",
        borderBottomLeftRadius: "6px",
      },
    },
    "& li:last-child": {
      "& .MuiButtonBase-root": {
        borderTopRightRadius: "6px",
        borderBottomRightRadius: "6px",
      },
    },
  },
}));

export const NoDataContainer = styled(Box)(() => ({
  backgroundColor: "#fff",
  width: "100%",
  padding: "40px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

export const StickyStyle: SxProps = {
  position: "sticky",
  zIndex: 100,
};

export const pageSize: number = 10;

export const paddedPageSize: number = 16;

export const ROWS_PER_PAGE_OPTIONS: number[] = [10, 20, 50, 100];

export const MIN_ROWS_PER_PAGE: number = Math.min(...ROWS_PER_PAGE_OPTIONS);

export const MAX_ROWS_PER_PAGE: number = Math.max(...ROWS_PER_PAGE_OPTIONS);
